import { FieldMapEditor } from './FieldMapEditor';
import type { PaigeContentCentralType, PaigeFieldMapping, PaigeFilingType } from './paige.global';
import { fieldNamesOf, useJobTypes } from './use-job-field-names';

export interface ContentCentralTypeMapProps {
  types: PaigeContentCentralType[];
  onChange: (types: PaigeContentCentralType[]) => void;
  /** What a test listed: `documentType`, and `fields:<type>` / `required:<type>` per Content Central type. */
  choices?: Record<string, string[]>;
}

/**
 * The job's table columns as field map lines: a line maps one to the Content Central line-item field
 * that takes it (the names the export stage reads, `ContentCentralLineItems.MapPrefix`).
 */
export const lineItemColumns = [
  'Line item: Description',
  'Line item: Quantity',
  'Line item: Unit Price',
  'Line item: Amount',
] as const;

/** The id of a Paige type's Content Central choice, so "Review matching" can bring it into view. */
export function typeSelectId(paigeType: string): string {
  return `paige-cc-type-${paigeType
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`;
}

/** The select's value for "no line: the Content Central type of the same name". */
const sameName = '\u0000same-name';

/**
 * A Content Central destination's type map, as CapturePoint files: each Paige document type under
 * its own Content Central document type with its own field map. A type left on "Same name" goes to
 * the Content Central type of that name, or is not sent when the catalog has none; "Not sent" sends
 * none of its documents. The Content Central types and their fields are what the last test listed.
 */
export function ContentCentralTypeMap({ types, onChange, choices }: ContentCentralTypeMapProps) {
  const jobTypes = useJobTypes();
  const catalogTypes = choices?.['documentType'] ?? [];
  const names = typeNames(jobTypes, types);
  const put = (name: string, line: PaigeContentCentralType | null) => {
    const others = types.filter((t) => !same(t.paigeType, name));
    onChange(line ? [...others, line] : others);
  };
  return (
    <fieldset className="paige-field-map paige-type-map">
      <legend>Document types</legend>
      <small>
        Each Paige document type is filed under a Content Central document type with its own field
        map. Same name: the Content Central type of that name, and not sent when there is none.
        {catalogTypes.length === 0 && ' Choose a catalog to list its document types.'}
      </small>
      {names.length === 0 && (
        <small>The job’s document types are listed once Paige has read the job’s setup.</small>
      )}
      {names.map((name) => (
        <TypeLine
          key={name.toLowerCase()}
          name={name}
          line={types.find((t) => same(t.paigeType, name))}
          fields={fieldsOf(jobTypes, name)}
          catalogTypes={catalogTypes}
          choices={choices}
          onChange={(line) => put(name, line)}
        />
      ))}
    </fieldset>
  );
}

interface TypeLineProps {
  name: string;
  line?: PaigeContentCentralType;
  fields: string[];
  catalogTypes: string[];
  choices?: Record<string, string[]>;
  onChange: (line: PaigeContentCentralType | null) => void;
}

/** Where a type's documents go: the line's Content Central type, else the same-named one (assumed until a test lists the catalog), else none. */
function targetOf(name: string, line: PaigeContentCentralType | undefined, catalogTypes: string[]) {
  if (line) return line.documentType;
  const match = catalogTypes.find((t) => same(t, name));
  return match ?? (catalogTypes.length === 0 ? name : '');
}

/** One Paige type: where it goes, and the field map for that Content Central type. */
function TypeLine(props: TypeLineProps) {
  const { name, line, fields, catalogTypes, choices, onChange } = props;
  const target = targetOf(name, line, catalogTypes);
  const map = line?.fieldMap ?? [];
  const choose = (value: string) =>
    onChange(value === sameName ? null : { paigeType: name, documentType: value, fieldMap: map });
  const setMap = (fieldMap: PaigeFieldMapping[]) =>
    onChange({ paigeType: name, documentType: target, fieldMap });
  return (
    <div className="paige-type-map-line">
      <TypeChoice name={name} line={line} catalogTypes={catalogTypes} onChoose={choose} />
      {target !== '' && (
        <>
          <RequiredNote required={choices?.[`required:${target}`]} map={map} fields={fields} />
          <FieldMapEditor
            map={map}
            onChange={setMap}
            legend={`Field map for ${target}`}
            hint="Fields not listed go under their own name. Leave the target blank to skip a field. A table’s columns (Line item: …) go to the line-item fields of the same meaning; map one when Content Central calls it something else."
            targetLabel="Content Central field"
            emptyTarget="Leave out"
            fields={fields.length > 0 ? [...fields, ...lineItemColumns] : undefined}
            targets={choices?.[`fields:${target}`]}
          />
        </>
      )}
    </div>
  );
}

/** The Paige type's name over its choice: same name, not sent, or one of the catalog's types. */
function TypeChoice(props: {
  name: string;
  line?: PaigeContentCentralType;
  catalogTypes: string[];
  onChoose: (value: string) => void;
}) {
  const { name, line, catalogTypes, onChoose } = props;
  const match = catalogTypes.find((t) => same(t, name));
  return (
    <label>
      {name}
      <select
        id={typeSelectId(name)}
        value={line ? line.documentType : sameName}
        onChange={(e) => onChoose(e.target.value)}
      >
        <option value={sameName}>{sameNameLabel(match, catalogTypes)}</option>
        <option value="">Not sent</option>
        {withSaved(catalogTypes, line?.documentType).map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
    </label>
  );
}

/** The Content Central fields this type requires that nothing fills: no line maps to them and no Paige field has the name. */
function RequiredNote(props: { required?: string[]; map: PaigeFieldMapping[]; fields: string[] }) {
  const { required = [], map, fields } = props;
  const filled = (field: string) =>
    map.some((m) => same(m.target, field)) ||
    fields.some(
      (f) => same(f, field) && !map.some((m) => same(m.field, f) && m.target.trim() === ''),
    );
  const open = required.filter((field) => !filled(field));
  if (required.length === 0) return null;
  return (
    <small
      className={open.length > 0 ? 'paige-type-map-required is-open' : 'paige-type-map-required'}
    >
      Content Central requires {required.join(', ')}
      {open.length > 0 && `; map a Paige field to ${open.join(', ')}`}
    </small>
  );
}

function sameNameLabel(match: string | undefined, catalogTypes: string[]): string {
  if (match) return `Same name (${match})`;
  return catalogTypes.length > 0 ? 'Same name (none: not sent)' : 'Same name';
}

/** The catalog's types, with a saved type the last test did not list kept as a choice. */
function withSaved(catalogTypes: string[], saved?: string): string[] {
  if (!saved || catalogTypes.some((t) => same(t, saved))) return catalogTypes;
  return [saved, ...catalogTypes];
}

/** The job's types, then any the map names that the job no longer lists. */
function typeNames(jobTypes: PaigeFilingType[], types: PaigeContentCentralType[]): string[] {
  const names = jobTypes.map((t) => t.name);
  for (const t of types) if (!names.some((n) => same(n, t.paigeType))) names.push(t.paigeType);
  return names;
}

function fieldsOf(jobTypes: PaigeFilingType[], name: string): string[] {
  const type = jobTypes.find((t) => same(t.name, name));
  return type ? fieldNamesOf({ types: [type] }) : [];
}

function same(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
